import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import {
  BotIcon,
  ChartNoAxesCombinedIcon,
  DatabaseZapIcon,
  GitPullRequestCreateArrow,
  RocketIcon,
  ScanIcon,
  Sparkles,
  UsersIcon,
  Wand2,
} from 'lucide-react';
import { ChevronRight } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@kit/ui/navigation-menu';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import pathsConfig from '~/config/paths.config';

export function ProductNav() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const router = useRouter();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const handleRoadmapClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDialogOpen(true);
  };

  const handleLogin = () => {
    setIsDialogOpen(false);
    router.push(`/auth/sign-in?next=${pathsConfig.app.personalAccountroadmap}`);
  };

  const handleGoBack = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (isDialogOpen) {
      dialogContentRef.current?.focus();
    }
  }, [isDialogOpen]);

  return (
    <>
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={cn(
                'inline-flex w-max text-sm font-semibold transition-colors duration-300',
                'hover:text-primary dark:hover:text-primary text-black dark:text-gray-300',
                'bg-transparent p-0 pt-[0.16rem] hover:bg-transparent focus:bg-transparent data-active:bg-transparent data-[state=open]:bg-transparent',
              )}
            >
              <Trans i18nKey="marketing:product.title" />
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="grid w-[672px] gap-3 p-4 md:grid-cols-2">
                <div className="grid gap-1 border-r border-gray-200 pr-4 dark:border-gray-800">
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/construction-site-monitoring"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <ChartNoAxesCombinedIcon className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.siteMonitoring.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.siteMonitoring.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/ai-project-assistant"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <BotIcon className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.aiAssistant.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.aiAssistant.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/vr"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <ScanIcon className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.vr.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.vr.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/collaboration-and-workflows"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <UsersIcon className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.collaboration.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.collaboration.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/cde-and-bim-viewer"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <DatabaseZapIcon className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.cde.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.cde.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/product/cpq-cto"
                      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block gap-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        <Wand2 className="hover:text-primary h-4 w-4 transition-colors" />
                        <span className="text-sm font-medium">
                          <Trans i18nKey="marketing:product.items.cpq.title" />
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        <Trans i18nKey="marketing:product.items.cpq.description" />
                      </p>
                    </Link>
                  </NavigationMenuLink>
                </div>
                <div className="grid content-start gap-4 p-4">
                  <div className="grid gap-1">
                    <div className="flex items-center gap-2">
                      <RocketIcon className="hover:text-primary h-4 w-4 transition-colors" />
                      <p className="text-sm font-medium">
                        <Trans i18nKey="marketing:product.weeklyReleases.title" />
                      </p>
                    </div>
                    <p className="text-muted-foreground line-clamp-4 text-sm">
                      <Trans i18nKey="marketing:product.weeklyReleases.description" />
                      <Link
                        href="/home/roadmap"
                        onClick={handleRoadmapClick}
                        className="hover:text-primary underline transition-colors"
                      >
                        <Trans i18nKey="marketing:roadmap" />
                      </Link>
                    </p>
                  </div>
                  <div className="mt-4 grid gap-1">
                    <h4 className="flex items-center gap-2 text-sm leading-none font-medium">
                      <GitPullRequestCreateArrow className="hover:text-primary h-4 w-4 transition-colors" />
                      <Trans i18nKey="marketing:product.latestUpdates.title" />
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      <Trans i18nKey="marketing:product.latestUpdates.description" />
                    </p>
                  </div>

                  <div className="bg-muted mt-4 rounded-md p-4">
                    <h4 className="flex items-center gap-2 text-sm leading-none font-medium">
                      <Sparkles className="hover:text-primary h-4 w-4 transition-colors" />
                      <Trans i18nKey="marketing:product.seeInAction.title" />
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      <Trans i18nKey="marketing:product.seeInAction.description" />
                    </p>
                    <Button asChild className="mt-2">
                      <Link href={'/auth/sign-in'}>
                        <span className={'flex items-center gap-0.5'}>
                          <span>
                            <Trans i18nKey={'common:getStarted'} />
                          </span>

                          <ChevronRight
                            className={
                              'animate-in fade-in slide-in-from-left-8 h-4' +
                              ' zoom-in fill-mode-both delay-800 duration-1000'
                            }
                          />
                        </span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent ref={dialogContentRef} tabIndex={-1}>
          <DialogHeader>
            <DialogTitle className="text-lg leading-none font-semibold tracking-tight">
              <Trans i18nKey={'marketing:authenticationRequired'} />
            </DialogTitle>
          </DialogHeader>

          <p className="text-muted-foreground text-center text-sm leading-normal">
            <Trans i18nKey="marketing:loginToRoadmap" />
          </p>

          <div className="mt-6 flex justify-center gap-4">
            <Button onClick={handleGoBack} variant={'outline'} size="sm">
              <Trans i18nKey="goBack" />
            </Button>
            <Button onClick={handleLogin} variant={'default'} size="sm">
              <Trans i18nKey="Login" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
